/**
 * @jsx jsx
 */
import type { PropsWithChildren } from 'react';
import React, { useState } from 'react';
import { jsx } from '@balance-web/core';
import { Inline } from '@balance-web/inline';
import { Flex } from '@balance-web/flex';
import { Stack } from '@balance-web/stack';
import { Button } from '@balance-web/button';
import { Text } from '@balance-web/text';
import { Divider } from '@balance-web/divider';
import { Toggle } from '@balance-web/toggle';
import { FieldLabel } from '@balance-web/field';

import { ReckonOneLogo } from '@reckon-web/application-logos';

import { AppLayout } from '../AppLayout';
import { EmptyState } from '../../common/EmptyState';
import { CodeBlock } from '../../common/CodeBlock';
import { serialiseWizardState } from '../../../core/Wizard';
import { Config } from '../../../services/Config';

import { useUserStepStatus } from './PickUserStep/useUserStepStatus';
import { useDevLauncherWizard } from './useDevLauncherWizard';
import { WizardStepButton } from './WizardStepButton';
type DevLauncherWizardProps = PropsWithChildren<{}>;
export const DevLauncherWizard = ({ children }: DevLauncherWizardProps) => {
  const [stateVisible, setStateVisible] = useState(false);
  const { wizard, resetWizard } = useDevLauncherWizard();
  const authenticatedUserStatus = useUserStepStatus();

  if (!wizard) {
    return null;
  }

  return (
    <AppLayout>
      <EmptyState
        fullScreen
        actions={[]}
        content={
          <Stack
            align="center"
            gap="large"
            margin="xxlarge"
            marginTop="medium"
            maxWidth={720}
          >
            <Text align="center">
              To simulate a user arriving here on "
              {Config.APPLICATION_DISPLAY_NAME}", complete the wizard to prepare
              the correct urlhash
            </Text>

            <Divider />

            <Inline gap="medium">
              <WizardStepButton
                step={1}
                {...authenticatedUserStatus}
                title="Authenticate as User"
              />
            </Inline>

            <Stack flexGrow={1} gap="large" marginTop="large" width="100%">
              {children}
            </Stack>

            <Divider />

            <Stack gap="large" marginTop="large" maxWidth={720} width="100%">
              <Flex alignSelf="end" justifyContent="space-between" width={320}>
                <FieldLabel htmlFor={'show-state-code'}>
                  Show wizard state
                </FieldLabel>
                <Toggle
                  checked={stateVisible}
                  id={'show-state-code'}
                  onChange={setStateVisible}
                />
              </Flex>
              {stateVisible && (
                <Stack gap="large" width="100%">
                  <Inline align="right">
                    <Button
                      block={false}
                      label="reset state"
                      size="small"
                      onClick={() => {
                        return resetWizard();
                      }}
                    />
                  </Inline>
                  <CodeBlock
                    code={serialiseWizardState({ wizard, prettify: true })}
                  />
                </Stack>
              )}
            </Stack>
          </Stack>
        }
        icon={<ReckonOneLogo size={64} />}
        title="Development Mode"
      />
    </AppLayout>
  );
};
